import {
  Box,
  Typography,
  Stack,
  TablePagination,
  Collapse,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import LoopIcon from "@mui/icons-material/Loop";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useState, useMemo, Fragment, useEffect } from "react";
import { useUserEventsQuery } from "../../../../hooks/api_hooks/userUserEvents";

const T = {
  bg: "#F7F8FC",
  surface: "#FFFFFF",
  border: "#E2E8F0",
  ink: "#0F172A",
  muted: "#64748B",
  faint: "#94A3B8",
  accent: "#09b2f0",
  accentBg: "#F0F9FF",
};

const STATUS_META = {
  request_sent: {
    label: "Request Sent",
    bg: "transparent",
    color: T.muted,
    dot: "#3B82F6",
    icon: <ArrowOutwardIcon sx={{ fontSize: 11 }} />,
  },
  request_in_progress: {
    label: "In Progress",
    bg: "transparent",
    color: T.muted,
    dot: "#F97316",
    icon: <LoopIcon sx={{ fontSize: 11 }} />,
  },
  data_deleted: {
    label: "Data Deleted",
    bg: "transparent",
    color: T.muted,
    dot: "#22C55E",
    icon: <CheckCircleOutlineIcon sx={{ fontSize: 11 }} />,
  },
};

const FILTERS = [
  { key: null, label: "All" },
  { key: "request_sent", label: "Request Sent", dot: "#3B82F6" },
  { key: "request_in_progress", label: "In Progress", dot: "#F97316" },
  { key: "data_deleted", label: "Data Deleted", dot: "#22C55E" },
];

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

function relativeDate(iso) {
  if (!iso) return "—";
  const now = new Date();
  const then = new Date(iso);
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const diffDays = Math.round((nowDay - thenDay) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.round(diffDays / 30.44);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  const diffYears = Math.round(diffDays / 365.25);
  return `${diffYears}yr ago`;
}

function StatusBadge({ status }) {
  const c = STATUS_META[status] || STATUS_META.request_sent;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        bgcolor: c.bg,
        px: 1,
        py: 0.4,
        borderRadius: 10,
        display: "inline-flex",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: c.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: c.color,
          whiteSpace: "nowrap",
        }}
      >
        {c.label}
      </Typography>
    </Stack>
  );
}

function FilterPills({ activeFilter, onChange }) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap">
      {FILTERS.map((f) => {
        const isActive = activeFilter === f.key;
        return (
          <Box
            key={String(f.key)}
            onClick={() => onChange(f.key)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.6,
              px: 1.25,
              py: 0.5,
              borderRadius: 10,
              cursor: "pointer",
              border: `1.5px solid ${isActive ? "#475569" : T.border}`,
              bgcolor: "transparent",
              transition: "all 0.15s",
              "&:hover": {
                borderColor: "#475569",
                bgcolor: "transparent",
              },
            }}
          >
            {f.dot && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: f.dot,
                  flexShrink: 0,
                }}
              />
            )}
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: isActive ? 700 : 500,
                color: isActive ? "#475569" : T.muted,
                whiteSpace: "nowrap",
              }}
            >
              {f.label}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function CopyChip({ value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      onClick={handleCopy}
      sx={{
        display: "inline-flex",
        border: `1px solid ${copied ? "#5DB82E" : T.border}`,
        borderRadius: 2,
        px: 1.25,
        py: 0.5,
        cursor: "pointer",
        bgcolor: copied ? "#E6F9D8" : "#F9FAFB",
        transition: "all 0.2s",
        "&:hover": { bgcolor: T.accentBg, borderColor: T.accent },
      }}
    >
      {copied ? (
        <CheckIcon sx={{ fontSize: 13, color: "#5DB82E" }} />
      ) : (
        <CopyIcon sx={{ fontSize: 13, color: T.faint }} />
      )}
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontFamily: "monospace",
          color: copied ? "#3A7D0A" : T.muted,
          letterSpacing: "0.02em",
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

const JOURNEY = [
  { key: "request_sent", label: "Sent" },
  { key: "request_in_progress", label: "In Progress" },
  { key: "data_deleted", label: "Deleted" },
];

function StepIcon({ isDone, isActive }) {
  if (isDone)
    return (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          bgcolor: "#22C55E",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M2 5l2.5 2.5L8 3"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </Box>
    );
  if (isActive)
    return (
      <Box
        sx={{
          position: "relative",
          width: 18,
          height: 18,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: "#475569",
            opacity: 0.15,
            animation: "pulse 1.5s ease-out infinite",
            "@keyframes pulse": {
              "0%": { transform: "scale(1)", opacity: 0.25 },
              "100%": { transform: "scale(2.2)", opacity: 0 },
            },
          }}
        />
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            bgcolor: "#475569",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }}
          />
        </Box>
      </Box>
    );
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: `2px solid ${T.border}`,
        bgcolor: "#fff",
        flexShrink: 0,
      }}
    />
  );
}

function JourneyBar({ status_timestamps, latest_status }) {
  const activeIdx = Math.max(
    0,
    JOURNEY.findIndex((s) => s.key === latest_status),
  );
  const allDone = latest_status === "data_deleted";

  return (
    <Box>
      <Stack direction="row" alignItems="center">
        {JOURNEY.map((step, idx) => {
          const isDone = allDone || idx < activeIdx;
          const isActive = idx === activeIdx && !allDone;
          return (
            <Fragment key={step.key}>
              <StepIcon isDone={isDone} isActive={isActive} />
              {idx < JOURNEY.length - 1 && (
                <Box sx={{ flex: 1, height: 1.5, bgcolor: "#CBD5E1" }} />
              )}
            </Fragment>
          );
        })}
      </Stack>
      <Stack direction="row" alignItems="flex-start" sx={{ mt: 1.25 }}>
        {JOURNEY.map((step, idx) => {
          const isDone = allDone || idx < activeIdx;
          const isActive = idx === activeIdx && !allDone;
          return (
            <Fragment key={step.key}>
              <Stack
                alignItems="center"
                sx={{
                  width: 80,
                  transform: idx === 1 ? "translateX(-31px)" : "none",
                  ml: idx === 0 ? 0 : idx === JOURNEY.length - 1 ? -8 : 0,
                  alignItems:
                    idx === 0
                      ? "flex-start"
                      : idx === JOURNEY.length - 1
                        ? "flex-end"
                        : "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: isDone ? T.ink : isActive ? "#475569" : T.faint,
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    color: T.faint,
                    mt: 0.25,
                    whiteSpace: "nowrap",
                  }}
                >
                  {status_timestamps?.[step.key]
                    ? formatDate(status_timestamps[step.key])
                    : "—"}
                </Typography>
              </Stack>
              {idx < JOURNEY.length - 1 && <Box flex={1} />}
            </Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}

function InitialsBox({ name }) {
  const initials = name
    .split(/(?=[A-Z])|\s/)
    .filter((w) => w.length > 0)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  return (
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: 2,
        bgcolor: T.surface,
        border: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
        {initials}
      </Typography>
    </Box>
  );
}

function RequestRow({ req, isLast }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ borderBottom: isLast ? "none" : `1px solid ${T.border}` }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          transition: "background 0.15s",
          bgcolor: open ? T.accentBg : "transparent",
          "&:hover": { bgcolor: T.accentBg },
          flexWrap: "nowrap",
        }}
      >
        <InitialsBox name={req.company} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: T.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {req.company}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: T.faint, mt: 0.1 }}>
            {relativeDate(req.updated_at || req.created_at)}
          </Typography>
        </Box>
        <StatusBadge status={req.latest_status} />
        <Box
          sx={{
            color: T.faint,
            display: "flex",
            alignItems: "center",
            ml: 0.5,
          }}
        >
          {open ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </Box>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: 3,
            py: 2.5,
            bgcolor: "#FAFBFD",
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: T.faint,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 1.5,
            }}
          >
            Removal Journey
          </Typography>
          <JourneyBar
            status_timestamps={req.status_timestamps}
            latest_status={req.latest_status}
          />
          <Divider sx={{ my: 2, borderColor: T.border }} />
          <Stack
            direction="row"
            spacing={4}
            alignItems="flex-start"
            flexWrap="wrap"
            sx={{ gap: 2 }}
          >
            <Stack spacing={0.4}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: T.faint,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                Created
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: T.ink, fontWeight: 500 }}
              >
                {formatDate(req.created_at)}
              </Typography>
            </Stack>
            <Stack spacing={0.4}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: T.faint,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                Last Updated
              </Typography>
              <Typography
                sx={{ fontSize: "0.82rem", color: T.ink, fontWeight: 500 }}
              >
                {formatDate(req.updated_at)}
              </Typography>
            </Stack>
            <Stack spacing={0.4}>
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  color: T.faint,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}
              >
                Request ID
              </Typography>
              <CopyChip value={req.request_id} />
            </Stack>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}

function RequestRowSkeleton({ isLast }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        borderBottom: isLast ? "none" : `1px solid ${T.border}`,
      }}
    >
      <Skeleton
        variant="rounded"
        width={38}
        height={38}
        sx={{ borderRadius: 2, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width={160} height={16} />
        <Skeleton variant="text" width={80} height={13} sx={{ mt: 0.5 }} />
      </Box>
      <Skeleton
        variant="rounded"
        width={90}
        height={22}
        sx={{ borderRadius: 10 }}
      />
      <Skeleton
        variant="circular"
        width={20}
        height={20}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
}

function Card({ children, sx = {} }) {
  return (
    <Box
      sx={{
        bgcolor: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: "16px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function SearchBar({ value, onChange, onSubmit, onClear }) {
  const handleKeyDown = (e) => {
    if (e.key === "Enter") onSubmit();
  };
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ position: "relative", width: 220 }}>
        <Box
          sx={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            color: T.faint,
            display: "flex",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle
              cx="11"
              cy="11"
              r="7"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M16.5 16.5L21 21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </Box>
        <input
          type="text"
          placeholder="Search brokers..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            width: "100%",
            paddingLeft: 30,
            paddingRight: value ? 28 : 10,
            paddingTop: 6,
            paddingBottom: 6,
            fontSize: "0.8rem",
            border: `1.5px solid ${T.border}`,
            borderRadius: 8,
            outline: "none",
            background: T.surface,
            color: T.ink,
            boxSizing: "border-box",
            fontFamily: "inherit",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = T.accent;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = T.border;
          }}
        />
        {value && (
          <Box
            onClick={onClear}
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: T.faint,
              display: "flex",
              "&:hover": { color: T.muted },
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Box>
        )}
      </Box>
      <Box
        onClick={onSubmit}
        sx={{
          px: 1.5,
          py: 0.75,
          bgcolor: "#f59e0b",
          borderRadius: 2,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          transition: "opacity 0.15s",
          "&:hover": { opacity: 0.85 },
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "#000",
            whiteSpace: "nowrap",
          }}
        >
          Search
        </Typography>
      </Box>
    </Stack>
  );
}

export default function Requests() {
  const [activeFilter, setActiveFilter] = useState(null);
  const [localSearch, setLocalSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");

  const { data, isLoading, isError } = useUserEventsQuery({ limit: Infinity });

  const allRequests = useMemo(() => data?.items ?? [], [data]);

  // Auto-clear committed search when user backspaces to empty
  useEffect(() => {
    if (localSearch === "") {
      setCommittedSearch("");
      setClientPage(0);
    }
  }, [localSearch]);

  const filteredRequests = useMemo(() => {
    let result = allRequests;
    if (activeFilter) {
      result = result.filter((r) => r.latest_status === activeFilter);
    }
    if (committedSearch.trim()) {
      const q = committedSearch.trim().toLowerCase();
      result = result.filter((r) => r.company?.toLowerCase().includes(q));
    }
    return result;
  }, [allRequests, activeFilter, committedSearch]);

  const ROWS_PER_PAGE = 15;
  const [clientPage, setClientPage] = useState(0);

  const paginatedRequests = useMemo(() => {
    const start = clientPage * ROWS_PER_PAGE;
    return filteredRequests.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRequests, clientPage]);

  const handleFilterChange = (key) => {
    setActiveFilter(key);
    setClientPage(0);
  };

  const onSearchChange = (val) => setLocalSearch(val);

  const onSearchSubmit = () => {
    setCommittedSearch(localSearch);
    setClientPage(0);
  };

  const onSearchClear = () => {
    setLocalSearch("");
    setCommittedSearch("");
    setClientPage(0);
  };

  return (
    // FIX 1: Added overflowX: "hidden" to prevent horizontal overflow bounce on mobile
    <Box
      sx={{
        maxWidth: 1200,
        mx: "auto",
        py: { xs: 4, md: 6 },
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        mt: 6,
        pb: 4,
        bgcolor: "#fafafa",
        overflowX: "hidden",
      }}
    >
      {/* FIX 2: Tightened px on mobile from 4 → 2 so content doesn't press against edges */}
      <Box sx={{ width: "100%", maxWidth: "1440px", px: { xs: 2, md: 4 } }}>
        <Card>
          {/* Header row: title + search */}
          <Box
            sx={{
              px: 2.5,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              borderBottom: `1px solid #F1F5F9`,
              backgroundColor: "#FAFBFD",
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: T.ink,
                  lineHeight: 1.3,
                }}
              >
                Deletion Requests
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: T.faint,
                  mt: 0.25,
                  display: "block",
                }}
              >
                {committedSearch && activeFilter
                  ? `"${committedSearch}" · ${STATUS_META[activeFilter]?.label}`
                  : committedSearch
                    ? `Showing results for "${committedSearch}"`
                    : activeFilter
                      ? `Filtered by: ${STATUS_META[activeFilter]?.label}`
                      : "Track the status of your data removal requests"}
              </Typography>
            </Box>
            <SearchBar
              value={localSearch}
              onChange={onSearchChange}
              onSubmit={onSearchSubmit}
              onClear={onSearchClear}
            />
          </Box>

          {/* Filter pills row */}
          <Box
            sx={{
              px: 2.5,
              py: 1.25,
              borderBottom: `1px solid ${T.border}`,
              bgcolor: T.surface,
            }}
          >
            <FilterPills
              activeFilter={activeFilter}
              onChange={handleFilterChange}
            />
          </Box>

          {isLoading && (
            <Box>
              {[...Array(10)].map((_, i) => (
                <RequestRowSkeleton key={i} isLast={i === 9} />
              ))}
            </Box>
          )}

          {!isLoading && filteredRequests.length === 0 && (
            <Stack alignItems="center" py={6} spacing={1}>
              <Typography sx={{ fontSize: "0.9rem", color: T.faint }}>
                {isError
                  ? "Failed to load requests."
                  : committedSearch && activeFilter
                    ? `No "${STATUS_META[activeFilter]?.label}" results for "${committedSearch}".`
                    : committedSearch
                      ? `No results for "${committedSearch}".`
                      : activeFilter
                        ? `No requests with status "${STATUS_META[activeFilter]?.label}".`
                        : "No requests found."}
              </Typography>
              {(committedSearch || activeFilter) && !isError && (
                <Box
                  onClick={() => {
                    onSearchClear();
                    handleFilterChange(null);
                  }}
                  sx={{
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    color: T.accent,
                    fontWeight: 600,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Clear all filters
                </Box>
              )}
            </Stack>
          )}

          {!isLoading && filteredRequests.length > 0 && (
            <Box>
              {paginatedRequests.map((req, i) => (
                <RequestRow
                  key={req.request_id}
                  req={req}
                  isLast={i === paginatedRequests.length - 1}
                />
              ))}
            </Box>
          )}
        </Card>

        {!isLoading && filteredRequests.length > 0 && (
          <TablePagination
            component="div"
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            page={clientPage}
            onPageChange={(_, newPage) => setClientPage(newPage)}
            count={filteredRequests.length}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} of ${count}`
            }
            sx={{ color: T.muted, "& .MuiTablePagination-toolbar": { px: 0 } }}
          />
        )}
      </Box>
    </Box>
  );
}
